export const DASHBOARD_PERMISSIONS = Object.freeze({
  OVERVIEW: "dashboard.overview",
  CUSTOMERS: "dashboard.customers",
  PROVIDERS: "dashboard.providers",
  PROVIDER_VERIFICATION: "dashboard.providerVerification",
  SERVICES: "dashboard.services",
  CATEGORIES: "dashboard.categories",
  COUPONS: "dashboard.coupons",
  BOOKINGS: "dashboard.bookings",
  OPEN_POOL: "dashboard.openPool",
  REFUNDS: "dashboard.refunds",
  MARKETING: "dashboard.marketing",
  EMAIL_TEMPLATES: "dashboard.emailTemplates",
  SMS_TEMPLATES: "dashboard.smsTemplates",
  PAYOUT_SETTINGS: "dashboard.payoutSettings",
  HELPDESK: "dashboard.helpdesk",
  POLICIES: "dashboard.policies",
  TEAM_ACCESS: "dashboard.teamAccess",
});

export const DASHBOARD_PERMISSION_OPTIONS = [
  {
    key: DASHBOARD_PERMISSIONS.OVERVIEW,
    label: "Dashboard",
    group: "Overview",
    description: "Access the dashboard home and summary cards.",
  },
  {
    key: DASHBOARD_PERMISSIONS.CUSTOMERS,
    label: "Customers",
    group: "Users",
    description: "View and manage customer accounts.",
  },
  {
    key: DASHBOARD_PERMISSIONS.PROVIDERS,
    label: "Provider",
    group: "Users",
    description: "View and manage service provider accounts.",
  },
  {
    key: DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION,
    label: "Provider Verification",
    group: "Users",
    description: "Review and approve provider onboarding requests.",
  },
  {
    key: DASHBOARD_PERMISSIONS.SERVICES,
    label: "Services",
    group: "Services and Bookings",
    description: "Create, update, and manage services.",
  },
  {
    key: DASHBOARD_PERMISSIONS.CATEGORIES,
    label: "Categories",
    group: "Services and Bookings",
    description: "Manage service categories.",
  },
  {
    key: DASHBOARD_PERMISSIONS.COUPONS,
    label: "Coupons",
    group: "Services and Bookings",
    description: "Create and manage promotional coupons.",
  },
  {
    key: DASHBOARD_PERMISSIONS.BOOKINGS,
    label: "Bookings",
    group: "Services and Bookings",
    description: "View and manage booking records.",
  },
  {
    key: DASHBOARD_PERMISSIONS.OPEN_POOL,
    label: "Open Pool",
    group: "Services and Bookings",
    description: "Reassign providers for pending/rejected bookings.",
  },
  {
    key: DASHBOARD_PERMISSIONS.REFUNDS,
    label: "Refunds",
    group: "Services and Bookings",
    description: "Process and track refunds.",
  },
  {
    key: DASHBOARD_PERMISSIONS.HELPDESK,
    label: "Support Cases",
    group: "Support",
    description: "View and respond to customer and provider support cases.",
  },
  {
    key: DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS,
    label: "Payout Settings",
    group: "Settings",
    description: "Update commission, platform fee, and payout settings.",
  },
  {
    key: DASHBOARD_PERMISSIONS.MARKETING,
    label: "Marketing",
    group: "Settings",
    description: "Send targeted push notifications and email campaigns.",
  },
  {
    key: DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES,
    label: "Email Templates",
    group: "Settings",
    description: "Manage email template content.",
  },
  {
    key: DASHBOARD_PERMISSIONS.SMS_TEMPLATES,
    label: "Notification Templates",
    group: "Settings",
    description: "Manage push notification template content.",
  },
  {
    key: DASHBOARD_PERMISSIONS.POLICIES,
    label: "Policy and Terms",
    group: "Settings",
    description: "Manage legal policy and terms pages.",
  },
  {
    key: DASHBOARD_PERMISSIONS.TEAM_ACCESS,
    label: "Team Access",
    group: "Settings",
    description: "Create teams and assign dashboard access permissions.",
  },
];

export const DASHBOARD_ROUTE_PERMISSIONS = Object.freeze({
  "/dashboard": DASHBOARD_PERMISSIONS.OVERVIEW,
  "/dashboard/customers": DASHBOARD_PERMISSIONS.CUSTOMERS,
  "/dashboard/clients": DASHBOARD_PERMISSIONS.PROVIDERS,
  "/dashboard/client-approvals": DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION,
  "/dashboard/services": DASHBOARD_PERMISSIONS.SERVICES,
  "/dashboard/service-requests": DASHBOARD_PERMISSIONS.SERVICES,
  "/dashboard/categories": DASHBOARD_PERMISSIONS.CATEGORIES,
  "/dashboard/category-requests": DASHBOARD_PERMISSIONS.CATEGORIES,
  "/dashboard/coupons": DASHBOARD_PERMISSIONS.COUPONS,
  "/dashboard/bookings": DASHBOARD_PERMISSIONS.BOOKINGS,
  "/dashboard/payment-lifecycle": DASHBOARD_PERMISSIONS.BOOKINGS,
  "/dashboard/reassign-bookings": DASHBOARD_PERMISSIONS.OPEN_POOL,
  "/dashboard/reassign-leads": DASHBOARD_PERMISSIONS.OPEN_POOL,
  "/dashboard/refunds": DASHBOARD_PERMISSIONS.REFUNDS,
  "/dashboard/marketing": DASHBOARD_PERMISSIONS.MARKETING,
  "/dashboard/support": DASHBOARD_PERMISSIONS.HELPDESK,
  "/dashboard/helpdesk": DASHBOARD_PERMISSIONS.HELPDESK,
  "/dashboard/settings": DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS,
  "/dashboard/communication": DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES,
  "/dashboard/notifications": DASHBOARD_PERMISSIONS.SMS_TEMPLATES,
  "/dashboard/sms": DASHBOARD_PERMISSIONS.SMS_TEMPLATES,
  "/dashboard/policies": DASHBOARD_PERMISSIONS.POLICIES,
  "/dashboard/privacy-policy": DASHBOARD_PERMISSIONS.POLICIES,
  "/dashboard/terms-provider": DASHBOARD_PERMISSIONS.POLICIES,
  "/dashboard/terms-customer": DASHBOARD_PERMISSIONS.POLICIES,
  "/dashboard/team-access": DASHBOARD_PERMISSIONS.TEAM_ACCESS,
});

export const DASHBOARD_PERMISSION_ROUTE_ORDER = [
  { path: "/dashboard", permission: DASHBOARD_PERMISSIONS.OVERVIEW },
  { path: "/dashboard/customers", permission: DASHBOARD_PERMISSIONS.CUSTOMERS },
  { path: "/dashboard/clients", permission: DASHBOARD_PERMISSIONS.PROVIDERS },
  {
    path: "/dashboard/client-approvals",
    permission: DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION,
  },
  { path: "/dashboard/services", permission: DASHBOARD_PERMISSIONS.SERVICES },
  {
    path: "/dashboard/service-requests",
    permission: DASHBOARD_PERMISSIONS.SERVICES,
  },
  { path: "/dashboard/categories", permission: DASHBOARD_PERMISSIONS.CATEGORIES },
  { path: "/dashboard/category-requests", permission: DASHBOARD_PERMISSIONS.CATEGORIES },
  { path: "/dashboard/coupons", permission: DASHBOARD_PERMISSIONS.COUPONS },
  { path: "/dashboard/bookings", permission: DASHBOARD_PERMISSIONS.BOOKINGS },
  {
    path: "/dashboard/payment-lifecycle",
    permission: DASHBOARD_PERMISSIONS.BOOKINGS,
  },
  {
    path: "/dashboard/reassign-bookings",
    permission: DASHBOARD_PERMISSIONS.OPEN_POOL,
  },
  {
    path: "/dashboard/reassign-leads",
    permission: DASHBOARD_PERMISSIONS.OPEN_POOL,
  },
  { path: "/dashboard/refunds", permission: DASHBOARD_PERMISSIONS.REFUNDS },
  {
    path: "/dashboard/marketing",
    permission: DASHBOARD_PERMISSIONS.MARKETING,
  },
  {
    path: "/dashboard/communication",
    permission: DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES,
  },
  {
    path: "/dashboard/notifications",
    permission: DASHBOARD_PERMISSIONS.SMS_TEMPLATES,
  },
  {
    path: "/dashboard/settings",
    permission: DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS,
  },
  { path: "/dashboard/support", permission: DASHBOARD_PERMISSIONS.HELPDESK },
  { path: "/dashboard/policies", permission: DASHBOARD_PERMISSIONS.POLICIES },
  {
    path: "/dashboard/team-access",
    permission: DASHBOARD_PERMISSIONS.TEAM_ACCESS,
  },
];

export const SUGGESTED_TEAM_TEMPLATES = [
  {
    id: "mail-team",
    name: "Mail Team",
    description: "Can access marketing sends and email templates for communication tasks.",
    permissions: [DASHBOARD_PERMISSIONS.MARKETING, DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES],
  },
  {
    id: "verify-provider-team",
    name: "Verify Provider Team",
    description: "Can access only provider verification requests.",
    permissions: [DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION],
  },
  {
    id: "refund-team",
    name: "Refund Team",
    description: "Can access only refund workflow.",
    permissions: [DASHBOARD_PERMISSIONS.REFUNDS],
  },
];
